import React, { useState, useEffect } from 'react';
import { FeedbackState } from '../types';
import { getEloTier, getNextTier, getProgressToNextTier } from '../../../utils/eloUtils';
import EloChangePopup from './EloChangePopup';
import clsx from 'clsx';

interface Resource {
  url: string;
  type: 'documentation' | 'tutorial' | string;
  title: string;
  language: string;
}

interface ChallengeFeedbackProps {
  feedback: FeedbackState;
  userStats: {
    elo_rating: number;
    previous_elo?: number;
  } | null;
}

const ResourceIcon = ({ type }: { type: string }) => {
  switch (type) {
    case 'documentation':
      return (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      );
    case 'tutorial':
      return (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
            d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
        </svg>
      );
    default:
      return (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
        </svg>
      );
  }
};

const ChallengeFeedback: React.FC<ChallengeFeedbackProps> = ({
  feedback,
  userStats
}) => {
  const [showEloChange, setShowEloChange] = useState(false);

  useEffect(() => {
    if (userStats?.previous_elo && userStats.elo_rating !== userStats.previous_elo) {
      setShowEloChange(true);
    }
  }, [userStats]);

  if (!feedback.isVisible) return null;

  const currentTier = getEloTier(userStats?.elo_rating || 0);
  const nextTier = getNextTier(currentTier);
  const progress = getProgressToNextTier(userStats?.elo_rating || 0, currentTier);

  return (
    <>
      {showEloChange && userStats && (
        <EloChangePopup
          oldElo={userStats.previous_elo || 0}
          newElo={userStats.elo_rating}
          onClose={() => setShowEloChange(false)}
        />
      )}
      <div className={clsx(
        'card shadow-xl',
        feedback.isSuccess ? 'bg-success/10' : 'bg-error/10',
        'mb-4'
      )}>
        <div className="card-body">
          {/* Header */}
          <div className={clsx(
            'flex items-center gap-3 mb-4',
            feedback.isSuccess ? 'text-success' : 'text-error'
          )}>
            {feedback.isSuccess ? (
              <>
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                <h2 className="card-title">Excelente trabalho! 🎉</h2>
              </>
            ) : (
              <>
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
                <h2 className="card-title">Não se preocupe!</h2>
              </>
            )}
          </div>

          {/* ELO Progress */}
          {userStats && (
            <div className="mb-4">
              <p className="text-base-content/70 mb-2">Seu progresso:</p>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill={currentTier.color}>
                    <path d={currentTier.icon} />
                  </svg>
                  <span>{currentTier.name}</span>
                </div>
                
                <div className="flex-1">
                  <progress 
                    className="progress progress-info w-full" 
                    value={progress} 
                    max="100"
                  />
                </div>

                {nextTier && (
                  <div className="flex items-center gap-2 opacity-50">
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill={nextTier.color}>
                      <path d={nextTier.icon} />
                    </svg>
                    <span>{nextTier.name}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Explanation */}
          {feedback.explanation && (
            <div className="mb-4">
              <h3 className="font-bold text-lg mb-2">Explicação</h3>
              <p className="text-base-content/80">{feedback.explanation}</p>
            </div>
          )}

          {/* Solution */}
          {feedback.solution && (
            <div className="mb-4">
              <h3 className="font-bold text-lg mb-2">Solução</h3>
              <div className="bg-base-300 p-4 rounded-lg font-mono">
                {feedback.solution}
              </div>
            </div>
          )}

          {/* Resources */}
          {feedback.resources?.length > 0 && (
            <div>
              <h3 className="font-bold text-lg mb-2">Recursos para Aprender Mais</h3>
              <div className="flex flex-col gap-2">
                {(feedback.resources as Resource[]).map((resource, index) => (
                  <a
                    key={index}
                    href={resource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-ghost justify-start gap-2 normal-case hover:bg-base-200"
                  >
                    <ResourceIcon type={resource.type} />
                    <span>{resource.title}</span>
                    <span className="badge badge-sm ml-auto">{resource.language}</span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ChallengeFeedback;
